import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, RefreshCw, ListTree } from 'lucide-react';
import Button from '../ui/Button';
import type { DiagnosticResult, FieldCommand, EspNowRouter } from '../../services/espnowIrrigation';
import { buildActivity, activityFallback, type ActivityTone } from '../../utils/commandActivity';
import { coverageNote } from '../../utils/diagnosticCoverage';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import type { Transport } from '../../utils/nodeLabel';

/**
 * What this site has lately been asked to do, checks and commands together.
 *
 * Started life as "Recent checks" inside the mesh diagnostics panel, reading
 * only the diagnostics feed. That made the panel answer for Read Signal and
 * Scan Mesh and stay silent about the valve somebody opened a minute ago, the
 * program they forced, or the schedule that went out at midnight — all of which
 * were in the command log on a different tab, in a different shape.
 *
 * Both feeds now land here in one chronological list, which is the order the
 * question is actually asked in: "I pressed things, then nothing watered — in
 * what order did that happen, and what did the site say about each?"
 */

const ICON: Record<ActivityTone, React.ReactNode> = {
  success: <CheckCircle2 className="w-4 h-4 text-green-600" />,
  error: <AlertCircle className="w-4 h-4 text-red-600" />,
  warn: <AlertTriangle className="w-4 h-4 text-amber-600" />,
  pending: <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />,
  stale: <AlertTriangle className="w-4 h-4 text-amber-600" />,
};

const TONE: Record<ActivityTone, string> = {
  success: 'text-gray-600',
  error: 'text-red-600',
  warn: 'text-amber-700',
  pending: 'text-blue-600',
  stale: 'text-amber-700',
};

interface Props {
  /** The diagnostics feed. Empty on a site that has no mesh diagnostics. */
  results: DiagnosticResult[];
  /** The command log, as the controller page already fetches it. */
  commands: FieldCommand[];
  routers: EspNowRouter[];
  transport: Transport;
  /**
   * Heading, so the mesh panel and a standalone card can word it for context.
   * `null` drops it entirely, for a caller whose own heading already names
   * what the list is about — two headings in a row is one too many.
   */
  title?: string | null;
  /** How many rows to show. The full log lives on the Events tab. */
  limit?: number;
  /**
   * A notch smaller, for the network map's node drawer — a sidebar that is the
   * whole page on a phone and sets everything in it at `text-xs`. A panel that
   * arrives a size larger than its container reads as a different component
   * that has landed in the wrong place.
   */
  compact?: boolean;
  onRefresh?: () => void;
  /** Jump to the Events tab, where nothing is truncated. */
  onShowLog?: () => void;
}

export default function RecentActivity({
  results, commands, routers, transport,
  title = 'Recent activity', limit = 10, compact = false, onRefresh, onShowLog,
}: Props) {
  const tz = useOrgTimezone();
  const rows = buildActivity({ results, commands, routers, limit });

  if (rows.length === 0) return null;

  const rowText = compact ? 'text-xs' : 'text-sm';
  const subText = compact ? 'text-[11px]' : 'text-xs';

  return (
    <div>
      {(title || onShowLog || onRefresh) && (
        <div className="flex items-center justify-between gap-2 mb-2">
          <h4 className={`${rowText} font-medium text-gray-700`}>{title}</h4>
          <div className="flex items-center gap-1 shrink-0">
            {onShowLog && (
              <Button variant="ghost" size="sm" onClick={onShowLog}>
                <ListTree className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Full log</span>
              </Button>
            )}
            {onRefresh && (
              <Button variant="ghost" size="sm" onClick={onRefresh} aria-label="Refresh activity">
                <RefreshCw className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      )}

      <ul className="space-y-2">
        {rows.map(row => {
          /* The totals in a check's ack are the BRIDGE's node count, not this
             controller's, and the two part company the moment a node is removed
             — leaving an ack that says nine sitting under today's date above
             eight cards. Reconciled here rather than left for the reader, who
             has no way to see the other list. */
          const coverage = row.check ? coverageNote(row.check, routers.length, transport) : null;
          return (
            <li key={row.id} className={`flex items-start gap-2 ${rowText}`}>
              <span className="mt-0.5 shrink-0">{ICON[row.tone]}</span>
              <div className="min-w-0">
                <span className="font-medium text-gray-800">{row.label}</span>
                {row.count > 1 && (
                  <span className={`ml-1.5 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 ${subText} font-medium`}>
                    ×{row.count}
                  </span>
                )}
                <span className="text-gray-500"> · {formatDateTimeInTz(row.at, tz)}</span>
                {row.target && (
                  <p className={`${subText} leading-snug break-words text-gray-500`}>{row.target}</p>
                )}
                <p className={`${subText} leading-snug break-words ${TONE[row.tone]}`}>
                  {row.detail || activityFallback(row.tone)}
                </p>
                {coverage && (
                  <p className={`${subText} leading-snug break-words text-amber-700`}>{coverage}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
